import { DownloadIcon, PlusIcon, Trash2Icon, UsersRoundIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import Link from '@/components/atoms/Link/Link';
import { Button } from '@/components/ui/button';
import SingleFileUpload from '@/components/ui/custom/FileUpload/SingleFileUpload';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Campaign, CampaignSubscriber, CampaignType } from '@/shared/types/campaigns';
import { CAMPAIGNS_DOCS_URL } from '@/util/constants';

import { PaymentRecoveryFilledContent } from './PaymentRecoveryFilledContent';
import { QuickAddPopover } from '../../../components/QuickAddPopover';
import { DraftCampaignSubscriber } from '../../../redux/actions';
import { isOutreachCampaign } from '../../../utils';
import { AudienceSummarySidebar } from '../../payment-recovery/components/AudienceSummarySidebar';

interface AudiencePanelProps {
  canWrite: boolean;
  campaignType: CampaignType;
  selectedFile: File | null;
  fileError: string;
  audienceCount: number;
  showDownloadSample: boolean;
  onFileSelect: (file: File | null) => Promise<void>;
  onDownloadSample: () => void;
  // Payment recovery specific
  campaign?: Campaign;
  hasUserDeletedAudience?: boolean;
  onMatchMissingFields?: () => void;
  onReviewAudience?: () => void;
  apiSubscribers?: CampaignSubscriber[];
  // Outreach specific
  onAddSubscriber?: (subscriber: DraftCampaignSubscriber) => void;
  existingSubscribers?: DraftCampaignSubscriber[];
  audienceStats?: {
    total_subscribers: number;
    email_subscribers: number;
    phone_subscribers: number;
    variables_count: number;
  };
  hasErrors?: boolean;
  audienceError?: string;
  isProcessing?: boolean;
  isQuickAddSource?: boolean;
}

const SKELETON_ROWS = [
  { label: 'w-16', value: 'w-20' },
  { label: 'w-36', value: 'w-20' },
  { label: 'w-24', value: 'w-8' },
];

const AudienceProcessingSkeleton = () => {
  const { t } = useTranslation();

  return (
    <div
      role="status"
      aria-label={t('file_upload.uploading')}
      className="flex flex-col gap-3"
      data-testid="audience-processing"
    >
      {SKELETON_ROWS.map((row) => (
        <div key={row.label}>
          <Skeleton className={cn('h-6 bg-stroke', row.label)} />
          <Skeleton className={cn('mt-1 h-6 bg-stroke', row.value)} />
        </div>
      ))}

      <Skeleton className="h-5 w-32 bg-stroke" />
    </div>
  );
};

/** Outreach audience summary when audience is uploaded */
const OutreachFilledContent = ({
  audienceStats,
  onReviewAudience,
}: {
  audienceStats?: {
    total_subscribers: number;
    email_subscribers: number;
    phone_subscribers: number;
    variables_count: number;
  };
  onReviewAudience?: () => void;
}) => {
  const { t } = useTranslation();

  if (!audienceStats) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="text-sm leading-6 text-foreground-muted">{t('campaigns.setup.emails')}</div>
        <div className="font-medium">{audienceStats.email_subscribers}</div>
      </div>

      <div>
        <div className="text-sm leading-6 text-foreground-muted">
          {t('campaigns.setup.phone_numbers')}
        </div>
        <div className="font-medium">{audienceStats.phone_subscribers}</div>
      </div>

      <div>
        <div className="text-sm leading-6 text-foreground-muted">
          {t('campaigns.setup.variables')}
        </div>
        <div className="font-medium">{audienceStats.variables_count}</div>
      </div>

      {onReviewAudience && (
        <button
          type="button"
          className="self-start text-sm font-medium text-primary hover:underline"
          onClick={onReviewAudience}
        >
          {t('campaigns.setup.review_audience')}
        </button>
      )}
    </div>
  );
};

export const AudiencePanel = ({
  canWrite,
  campaignType,
  selectedFile,
  fileError,
  audienceCount,
  showDownloadSample,
  onFileSelect,
  onDownloadSample,
  campaign,
  hasUserDeletedAudience,
  onMatchMissingFields,
  onReviewAudience,
  apiSubscribers,
  onAddSubscriber,
  existingSubscribers,
  audienceStats,
  hasErrors,
  audienceError,
  isProcessing,
  isQuickAddSource,
}: AudiencePanelProps) => {
  const { t } = useTranslation();

  const isOutreach = isOutreachCampaign(campaignType);
  const showQuickAdd =
    isOutreach &&
    canWrite &&
    (!selectedFile || isQuickAddSource) &&
    !isProcessing &&
    onAddSubscriber;
  const hasAudience = audienceCount > 0 && !hasUserDeletedAudience;
  const showAudienceSummary = hasAudience && !isProcessing;

  return (
    <div className="flex w-[500px] shrink-0 flex-col gap-4 px-8 pt-8">
      {/* Shared header — stable tree position so QuickAddPopover never unmounts */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isProcessing && <Skeleton className="h-7 w-28 bg-stroke" />}

          {!isProcessing && (
            <>
              <label
                className={cn(
                  hasAudience ? 'text-lg font-semibold' : 'text-sm font-medium',
                  !hasAudience && audienceError && 'text-warning'
                )}
              >
                {t('campaigns.setup.audience_label')}
              </label>
              {hasAudience && (
                <>
                  <span className="text-foreground-muted">·</span>
                  <UsersRoundIcon className="size-4 text-foreground-muted" />
                  <span className="text-sm font-medium">{audienceCount.toLocaleString()}</span>
                  {canWrite && (
                    <Button
                      variant="outline-neutral"
                      size="icon-sm"
                      onClick={() => onFileSelect(null)}
                      aria-label={t('common.delete')}
                      className="ml-1 hover:text-destructive hover:[&_svg]:text-destructive"
                    >
                      <Trash2Icon className="size-4" />
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </div>
        {showQuickAdd && (
          <QuickAddPopover
            onAdd={onAddSubscriber}
            existingSubscribers={existingSubscribers}
            align="end"
          >
            <Button
              type="button"
              variant="link"
              size="sm"
              className="flex h-6 items-center gap-2 text-accent hover:text-accent/80"
            >
              <PlusIcon size={16} />
              {t('campaigns.setup.quick_add')}
            </Button>
          </QuickAddPopover>
        )}
      </div>

      {/* Body content varies by state */}
      {isProcessing && <AudienceProcessingSkeleton />}

      {showAudienceSummary && (
        <>
          {isOutreach ? (
            <OutreachFilledContent
              audienceStats={audienceStats}
              onReviewAudience={onReviewAudience}
            />
          ) : (
            <PaymentRecoveryFilledContent
              onMatchMissingFields={onMatchMissingFields}
              onReviewAudience={onReviewAudience}
              apiSubscribers={apiSubscribers}
              hasErrors={hasErrors}
              suppressWarning={!!audienceError}
            />
          )}
          {/* Hard upload error on a replace leaves the old audience mounted (dropzone gone), so show it here too. */}
          {fileError && <p className="text-sm text-warning">{fileError}</p>}
          {audienceError && <p className="text-sm text-warning">{audienceError}</p>}
          {isOutreach && !audienceError && hasErrors && (
            <p className="text-sm text-warning">{t('campaigns.setup.some_invalid_subscribers')}</p>
          )}
        </>
      )}

      {!showAudienceSummary && !isProcessing && (
        <div className="space-y-2">
          <SingleFileUpload
            acceptedFormats={{ 'text/csv': ['.csv'] }}
            onFileSelect={onFileSelect}
            file={selectedFile}
            error={fileError}
            maxSizeMB={50}
            placeholder={t('file_upload.dnd_csv_file')}
            showAcceptedFormats={false}
            successTitleKey={t('campaigns.contacts_added', { count: audienceCount })}
            disabled={!canWrite}
          />

          <p className="text-sm text-foreground-muted">
            <Trans
              i18nKey="campaigns.setup.create_programmatically"
              components={{ link1: <Link href={CAMPAIGNS_DOCS_URL} external /> }}
            />
          </p>

          {audienceError && <p className="text-sm text-warning">{audienceError}</p>}

          {showDownloadSample && isOutreach && (
            <div className="flex justify-center pt-8">
              <Button variant="outline-neutral" size="sm" onClick={onDownloadSample}>
                <DownloadIcon size={16} />
                {t('campaigns.setup.download_template')}
              </Button>
            </div>
          )}

          {/* Empty state: payment recovery requirements + download */}
          {campaignType === 'payment_recovery' && (
            <AudienceSummarySidebar
              campaign={campaign}
              hasUserDeletedAudience={hasUserDeletedAudience}
              onMatchMissingFields={onMatchMissingFields}
              onReviewAudience={onReviewAudience}
              onDownloadSample={showDownloadSample ? onDownloadSample : undefined}
            />
          )}
        </div>
      )}
    </div>
  );
};
